import { adminApiClient } from '@/services/admin/baseService'
import type {
  AdminOrderSideEffectJobList,
  AdminOrderSideEffectJobSummary,
  AdminTestnetFaucetClaimList,
  AdminTestnetFaucetCooldownList,
  AdminTestnetFaucetItemList,
  AdminTestnetFaucetSummary,
  ApiResponse,
  FactoryReadOnlySnapshot,
  PlatformContractDescriptor,
  PlatformContractConsistencyResult,
  QueueQuarantineMessage,
  QueueQuarantineMessageList,
  QueueQuarantineSourceQueue,
  QueueQuarantineStatistic,
  QueueQuarantineStatus,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { getApiClientHeaderForMode } from '@/utils/apiConfig'

export interface MonitoringMetricsResponse {
  performance: {
    apiResponseTime?: number
    databaseLatency?: number
    settlementQueueDepth?: number
    webhookQueueDepth?: number
    gasPriceAgeSeconds?: number
    economyParamsAgeSeconds?: number
    webhookPendingCount?: number
    webhookRetryScheduledCount?: number
    webhookAvgQueueLagMs?: number
    webhookAvgDeliveryMs?: number
  }
  webhook?: {
    pendingCount?: number
    retryScheduledCount?: number
    avgQueueLagMs?: number
    avgDeliveryMs?: number
    statusBuckets?: {
      s2xx?: number
      s4xx?: number
      s5xx?: number
      timeoutOrNetwork?: number
    }
    diagnostics?: {
      eventsCreatedLast24h?: number
      activeMerchantsTotal?: number
      configuredMerchantsCount?: number
      missingSettingsCount?: number
      missingWebhookUrlCount?: number
      missingWebhookSecretCount?: number
    }
  }
  solana?: {
    initializationSuccessRate?: number | null
    avgDetectionLatencyMs?: number | null
    avgConfirmationLatencyMs?: number | null
    settlementSuccessRate?: number | null
    refundSuccessRate?: number | null
    treasurySuccessRate?: number | null
    rpcErrorRate?: number | null
    rpcSlotLag?: number | null
    latestRpcSlot?: number | null
    latestObservedSlot?: number | null
    diagnostics?: {
      ordersCreatedLast24h?: number
      paymentAddressReadyLast24h?: number
      deploymentRecordsLast24h?: number
      initializedContractsLast24h?: number
      paymentLogsLast24h?: number
      confirmedPaymentLogsLast24h?: number
      settlementRecordsLast24h?: number
      refundOperationsLast24h?: number
      settlementSweepsLast24h?: number
      paymentLogsWithSlotLast24h?: number
      settlementRecordsWithSlotLast24h?: number
      deploymentRecordsWithSlotLast24h?: number
    }
  }
  timestamp?: string
}

export type SolanaSignerLaneState =
  | 'idle'
  | 'preparing'
  | 'prepared'
  | 'broadcast'
  | 'unknown'
  | 'frozen'

export interface SolanaSignerLaneStatus {
  poolVersion: string
  laneIndex: number
  laneRevision: number
  prepareEpoch: number
  acceptsNewPrepare: boolean
  signerAddress: string
  nonceAccount: string
  status: SolanaSignerLaneState
  intentId: string | null
  operation: string | null
  businessReference: { type: string; id: string } | null
  signature: string | null
  startedAtMs: number | null
  expiresAtMs: number | null
  preparedAtMs: number | null
  firstBroadcastAtMs: number | null
  updatedAtMs: number | null
  latestEvidenceStatus: string | null
  latestEvidenceAtMs: number | null
  errorCode: string | null
  errorRetryable: boolean | null
  recoveryOwner: 'root' | 'signer' | null
  canReconcile: boolean
}

export interface SolanaSignerLaneStatusResponse {
  chainCode: string
  observedAtMs: number
  scope: {
    mode: 'mainnet' | 'testnet'
    numericChainId: number
    genesisHash: string
    role: 'deployment'
  }
  pool: {
    activeVersion: string
    acceptsNewPrepare: boolean
    activeCapacity: number
    activeBusy: number
    activeIdle: number
    frozen: number
    revision: number
  }
  alarm: {
    state: 'not_scheduled' | 'scheduled' | 'due'
    scheduledAtMs: number | null
  }
  lanes: SolanaSignerLaneStatus[]
  capabilities: { canView: boolean; canReconcile: boolean }
}

export type SolanaSignerRpcHealthState =
  | 'healthy'
  | 'degraded'
  | 'unavailable'
  | 'quarantined'
  | 'stale'
  | 'unknown'

export interface SolanaSignerRpcEndpointHealth {
  endpointHash: string
  status: 'healthy' | 'unavailable' | 'quarantined'
  lastValidatedAtMs: number | null
  lastFailureAtMs: number | null
  lastFailureCode: string | null
  consecutiveFailures: number
  lastLatencyMs: number
}

export interface SolanaSignerRpcHealthResponse {
  chainCode: string
  observedAtMs: number
  mode: 'mainnet' | 'testnet'
  numericChainId: number
  state: SolanaSignerRpcHealthState
  signerSecretVerified: boolean
  configuredEndpointHashes: string[]
  updatedAtMs: number | null
  nextProbeAtMs: number | null
  freshUntilMs: number | null
  endpoints: SolanaSignerRpcEndpointHealth[]
}

export interface SolanaSignerLaneExpectation {
  poolVersion: string
  laneIndex: number
  laneRevision: number
  prepareEpoch: number
  intentId: string
}

export interface SolanaSignerLaneReconcileResult {
  outcome: 'released' | 'continued' | 'pending'
  orderId: string
  provisioningState: string
  laneAfter: SolanaSignerLaneStatus | null
}

type ModeAwareRequestConfig = {
  headers: Record<string, string>
  __preserveAppClientHeader: boolean
}

const buildModeAwareConfig = (
  mode?: NetworkEnvironment
): ModeAwareRequestConfig | undefined => {
  if (!mode) {
    return undefined
  }
  return {
    headers: {
      'X-App-Client': getApiClientHeaderForMode(mode),
    },
    __preserveAppClientHeader: true,
  }
}

const QUEUE_QUARANTINE_MESSAGES_ERROR =
  'Failed to load queue quarantine messages'
const QUEUE_QUARANTINE_STATISTICS_ERROR =
  'Failed to load queue quarantine statistics'
const QUEUE_QUARANTINE_SOURCE_QUEUES_ERROR =
  'Failed to load queue quarantine source queues'

const emptyQueueQuarantineMessages = (params: {
  limit?: number
  offset?: number
}): QueueQuarantineMessageList => ({
  messages: [],
  pagination: {
    total: 0,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
    hasMore: false,
  },
})

const collectErrorText = (value: unknown): string => {
  if (!value) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (value instanceof Error) {
    const errorRecord = value as Error & {
      response?: unknown
      data?: unknown
      code?: unknown
      cause?: unknown
    }
    return [
      value.message,
      collectErrorText(errorRecord.response),
      collectErrorText(errorRecord.data),
      collectErrorText(errorRecord.code),
      collectErrorText(errorRecord.cause),
    ]
      .filter(Boolean)
      .join(' ')
  }
  if (Array.isArray(value)) {
    return value.map(collectErrorText).filter(Boolean).join(' ')
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    return [
      collectErrorText(record.error),
      collectErrorText(record.message),
      collectErrorText(record.code),
      collectErrorText(record.cause),
      collectErrorText(record.response),
      collectErrorText(record.data),
    ]
      .filter(Boolean)
      .join(' ')
  }
  return String(value)
}

const isQueueQuarantineTableUnavailable = (value: unknown): boolean => {
  const text = collectErrorText(value).toLowerCase()
  return (
    text.includes('queue_quarantine_messages') &&
    (text.includes('no such table') ||
      text.includes('does not exist') ||
      text.includes('table not found') ||
      text.includes('not found: table'))
  )
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const pickQueueField = (
  record: Record<string, unknown>,
  snakeKey: string,
  camelKey: string
): unknown => record[snakeKey] ?? record[camelKey]

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return ''
}

const toNullableStringValue = (value: unknown): string | null => {
  const text = toStringValue(value)
  return text.length > 0 ? text : null
}

const toNumberValue = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return fallback
}

const toBooleanValue = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') {
      return true
    }
    if (normalized === 'false' || normalized === '0' || normalized === '') {
      return false
    }
  }
  return fallback
}

const parseJsonValue = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value ?? null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  try {
    return JSON.parse(trimmed) as unknown
  } catch {
    return trimmed
  }
}

const normalizeJsonRecord = (value: unknown): Record<string, unknown> | null => {
  const parsed = parseJsonValue(value)
  return isRecord(parsed) ? parsed : null
}

const normalizeAuditTrail = (value: unknown): Array<Record<string, unknown>> | null => {
  const parsed = parseJsonValue(value)
  if (!Array.isArray(parsed)) {
    return null
  }
  return parsed.filter(isRecord)
}

const normalizeQueueStatus = (value: unknown): QueueQuarantineStatus => {
  if (value === 'open' || value === 'reprocessing' || value === 'resolved') {
    return value
  }
  return 'open'
}

const normalizeQueueCriticality = (
  value: unknown
): QueueQuarantineMessage['criticality'] => {
  if (
    value === 'financial' ||
    value === 'business_mutation' ||
    value === 'best_effort'
  ) {
    return value
  }
  return 'best_effort'
}

const normalizeQueueQuarantineMessage = (value: unknown): QueueQuarantineMessage => {
  const record = isRecord(value) ? value : {}
  const createdAt = toStringValue(pickQueueField(record, 'created_at', 'createdAt'))
  const lastSeenAt = toStringValue(pickQueueField(record, 'last_seen_at', 'lastSeenAt'))
  const updatedAt =
    toStringValue(pickQueueField(record, 'updated_at', 'updatedAt')) ||
    lastSeenAt ||
    createdAt

  return {
    id: toStringValue(record.id),
    schema_version: toNumberValue(
      pickQueueField(record, 'schema_version', 'schemaVersion'),
      1
    ),
    is_testnet: toBooleanValue(pickQueueField(record, 'is_testnet', 'isTestnet')),
    source_queue: toStringValue(pickQueueField(record, 'source_queue', 'sourceQueue')),
    original_message_id: toStringValue(
      pickQueueField(record, 'original_message_id', 'originalMessageId')
    ),
    message_type:
      toStringValue(pickQueueField(record, 'message_type', 'messageType')) || 'unknown',
    outcome: toStringValue(record.outcome) || 'quarantined',
    status: normalizeQueueStatus(record.status),
    criticality: normalizeQueueCriticality(record.criticality),
    attempts: toNumberValue(record.attempts, 0),
    payload_checksum: toStringValue(
      pickQueueField(record, 'payload_checksum', 'payloadChecksum')
    ),
    redacted_payload: parseJsonValue(
      pickQueueField(record, 'redacted_payload', 'redactedPayload')
    ),
    error_class:
      toStringValue(pickQueueField(record, 'error_class', 'errorClass')) || 'UnknownError',
    error_message: toNullableStringValue(
      pickQueueField(record, 'error_message', 'errorMessage')
    ),
    error_context: normalizeJsonRecord(
      pickQueueField(record, 'error_context', 'errorContext')
    ),
    lease_owner: toNullableStringValue(
      pickQueueField(record, 'lease_owner', 'leaseOwner')
    ),
    lease_expires_at: toNullableStringValue(
      pickQueueField(record, 'lease_expires_at', 'leaseExpiresAt')
    ),
    first_seen_at:
      toStringValue(pickQueueField(record, 'first_seen_at', 'firstSeenAt')) || createdAt,
    last_seen_at: lastSeenAt || createdAt,
    resolved_at: toNullableStringValue(
      pickQueueField(record, 'resolved_at', 'resolvedAt')
    ),
    resolved_by: toNullableStringValue(
      pickQueueField(record, 'resolved_by', 'resolvedBy')
    ),
    resolution_note: toNullableStringValue(
      pickQueueField(record, 'resolution_note', 'resolutionNote')
    ),
    audit_trail: normalizeAuditTrail(pickQueueField(record, 'audit_trail', 'auditTrail')),
    created_at: createdAt,
    updated_at: updatedAt,
  }
}

const normalizeQueueQuarantineList = (
  list: { messages?: unknown; pagination?: unknown },
  params: { limit?: number; offset?: number }
): QueueQuarantineMessageList => {
  const messages = Array.isArray(list.messages) ? list.messages : []
  const pagination = isRecord(list.pagination) ? list.pagination : {}

  return {
    messages: messages.map(normalizeQueueQuarantineMessage),
    pagination: {
      total: toNumberValue(pagination.total, messages.length),
      limit: toNumberValue(pagination.limit, params.limit ?? 50),
      offset: toNumberValue(pagination.offset, params.offset ?? 0),
      hasMore: toBooleanValue(pagination.hasMore),
    },
  }
}

const normalizeQueueQuarantineStatistic = (
  value: unknown
): QueueQuarantineStatistic => {
  const record = isRecord(value) ? value : {}
  return {
    is_testnet: toBooleanValue(pickQueueField(record, 'is_testnet', 'isTestnet')),
    status: normalizeQueueStatus(record.status),
    criticality: normalizeQueueCriticality(record.criticality),
    count: toNumberValue(record.count, 0),
  }
}

const normalizeQueueQuarantineSourceQueue = (
  value: unknown
): QueueQuarantineSourceQueue => {
  const record = isRecord(value) ? value : {}
  return {
    source_queue: toStringValue(pickQueueField(record, 'source_queue', 'sourceQueue')),
    count: toNumberValue(record.count, 0),
  }
}

const getApiErrorMessage = (
  response: ApiResponse<unknown> | undefined,
  fallback: string
): string => {
  const error = response?.error
  if (typeof error === 'string' && error) {
    return error
  }
  if (isRecord(error) && typeof error.message === 'string') {
    return error.message
  }
  if (typeof response?.message === 'string' && response.message) {
    return response.message
  }
  return fallback
}

const hasQueueQuarantineMessages = (
  value: unknown
): value is { messages: unknown[]; pagination?: unknown } =>
  isRecord(value) && Array.isArray(value.messages)

const resolveQueueQuarantineMessages = (
  value: unknown,
  params: { limit?: number; offset?: number }
): QueueQuarantineMessageList => {
  if (hasQueueQuarantineMessages(value)) {
    return normalizeQueueQuarantineList(value, params)
  }
  if (!isRecord(value)) {
    return emptyQueueQuarantineMessages(params)
  }
  const envelope = value as unknown as ApiResponse<unknown>
  if (envelope.success === true) {
    return hasQueueQuarantineMessages(envelope.data)
      ? normalizeQueueQuarantineList(envelope.data, params)
      : emptyQueueQuarantineMessages(params)
  }
  throw new Error(
    typeof envelope.error === 'string' && envelope.error
      ? envelope.error
      : QUEUE_QUARANTINE_MESSAGES_ERROR
  )
}

const resolveQueueQuarantineStatistics = (
  value: unknown
): QueueQuarantineStatistic[] => {
  if (isRecord(value) && Array.isArray(value.statistics)) {
    return value.statistics.map(normalizeQueueQuarantineStatistic)
  }
  if (!isRecord(value)) {
    return []
  }
  const envelope = value as unknown as ApiResponse<{
    statistics: QueueQuarantineStatistic[]
  }>
  if (envelope.success === true) {
    return (envelope.data?.statistics ?? []).map(normalizeQueueQuarantineStatistic)
  }
  throw new Error(
    typeof envelope.error === 'string' && envelope.error
      ? envelope.error
      : QUEUE_QUARANTINE_STATISTICS_ERROR
  )
}

const getSourceQueueArray = (record: Record<string, unknown>): unknown[] | null => {
  const sourceQueues = pickQueueField(record, 'source_queues', 'sourceQueues')
  return Array.isArray(sourceQueues) ? sourceQueues : null
}

const resolveQueueQuarantineSourceQueues = (
  value: unknown
): QueueQuarantineSourceQueue[] => {
  if (!isRecord(value)) {
    return []
  }
  const sourceQueues = getSourceQueueArray(value)
  if (sourceQueues) {
    return sourceQueues
      .map(normalizeQueueQuarantineSourceQueue)
      .filter(item => item.source_queue.length > 0)
  }
  const envelope = value as unknown as ApiResponse<unknown>
  if (envelope.success === true) {
    return resolveQueueQuarantineSourceQueues(envelope.data)
  }
  throw new Error(
    typeof envelope.error === 'string' && envelope.error
      ? envelope.error
      : QUEUE_QUARANTINE_SOURCE_QUEUES_ERROR
  )
}

export const monitoringService = {
  async getQueueQuarantineMessages(
    params: {
      limit?: number
      offset?: number
      status?: string
      queue?: string
    },
    mode: NetworkEnvironment
  ): Promise<QueueQuarantineMessageList> {
    try {
      const response = await adminApiClient.get<
        ApiResponse<QueueQuarantineMessageList> | QueueQuarantineMessageList
      >(
        '/admin/dlq/messages',
        {
          params: {
            ...params,
            is_testnet: mode === 'testnet',
          },
          ...buildModeAwareConfig(mode),
        }
      )
      if (isQueueQuarantineTableUnavailable(response.data)) {
        return emptyQueueQuarantineMessages(params)
      }
      return resolveQueueQuarantineMessages(response.data, params)
    } catch (error) {
      if (isQueueQuarantineTableUnavailable(error)) {
        return emptyQueueQuarantineMessages(params)
      }
      throw error
    }
  },

  async getQueueQuarantineStatistics(
    mode: NetworkEnvironment
  ): Promise<QueueQuarantineStatistic[]> {
    try {
      const response = await adminApiClient.get<
        | ApiResponse<{ statistics: QueueQuarantineStatistic[] }>
        | { statistics: QueueQuarantineStatistic[] }
      >('/admin/dlq/statistics', buildModeAwareConfig(mode))
      if (isQueueQuarantineTableUnavailable(response.data)) {
        return []
      }
      return resolveQueueQuarantineStatistics(response.data)
    } catch (error) {
      if (isQueueQuarantineTableUnavailable(error)) {
        return []
      }
      throw error
    }
  },

  async getQueueQuarantineSourceQueues(
    mode: NetworkEnvironment
  ): Promise<QueueQuarantineSourceQueue[]> {
    try {
      const response = await adminApiClient.get<
        | ApiResponse<{ sourceQueues: QueueQuarantineSourceQueue[] }>
        | { sourceQueues: QueueQuarantineSourceQueue[] }
      >('/admin/dlq/source-queues', buildModeAwareConfig(mode))
      if (isQueueQuarantineTableUnavailable(response.data)) {
        return []
      }
      return resolveQueueQuarantineSourceQueues(response.data)
    } catch (error) {
      if (isQueueQuarantineTableUnavailable(error)) {
        return []
      }
      throw error
    }
  },

  async getOrderSideEffectJobSummary(
    mode?: NetworkEnvironment
  ): Promise<AdminOrderSideEffectJobSummary> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderSideEffectJobSummary>>(
      '/admin/monitor/order-side-effect-jobs/summary',
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load side-effect job summary')
    }
    return response.data.data
  },

  async getOrderSideEffectJobs(
    params: {
      page?: number
      limit?: number
      orderId?: string
      status?: string
      jobType?: string
    },
    mode?: NetworkEnvironment
  ): Promise<AdminOrderSideEffectJobList> {
    const response = await adminApiClient.get<ApiResponse<AdminOrderSideEffectJobList>>(
      '/admin/monitor/order-side-effect-jobs',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(response.data?.error || 'Failed to load side-effect jobs')
    }
    return response.data.data
  },

  async getTestnetFaucetSummary(
    mode?: NetworkEnvironment
  ): Promise<AdminTestnetFaucetSummary> {
    const response = await adminApiClient.get<ApiResponse<AdminTestnetFaucetSummary>>(
      '/admin/monitor/testnet-faucet/summary',
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(getApiErrorMessage(response.data, 'Failed to load faucet summary'))
    }
    return response.data.data
  },

  async getTestnetFaucetClaims(
    params: {
      page?: number
      limit?: number
      status?: string
      packageType?: string
      userEmail?: string
      recipientAddress?: string
    },
    mode?: NetworkEnvironment
  ): Promise<AdminTestnetFaucetClaimList> {
    const response = await adminApiClient.get<ApiResponse<AdminTestnetFaucetClaimList>>(
      '/admin/monitor/testnet-faucet/claims',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(getApiErrorMessage(response.data, 'Failed to load faucet claims'))
    }
    return response.data.data
  },

  async getTestnetFaucetItems(
    claimId: string,
    mode?: NetworkEnvironment
  ): Promise<AdminTestnetFaucetItemList> {
    const response = await adminApiClient.get<ApiResponse<AdminTestnetFaucetItemList>>(
      '/admin/monitor/testnet-faucet/items',
      {
        params: { claimId },
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(getApiErrorMessage(response.data, 'Failed to load faucet claim items'))
    }
    return response.data.data
  },

  async getTestnetFaucetCooldowns(
    params: {
      page?: number
      limit?: number
      context?: string
      status?: string
      identifier?: string
    },
    mode?: NetworkEnvironment
  ): Promise<AdminTestnetFaucetCooldownList> {
    const response = await adminApiClient.get<ApiResponse<AdminTestnetFaucetCooldownList>>(
      '/admin/monitor/testnet-faucet/cooldowns',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(getApiErrorMessage(response.data, 'Failed to load faucet cooldowns'))
    }
    return response.data.data
  },

  async getMetrics(mode?: NetworkEnvironment): Promise<MonitoringMetricsResponse> {
    const response = await adminApiClient.get<ApiResponse<MonitoringMetricsResponse>>(
      '/admin/monitor/metrics',
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data) {
      throw new Error(response.data?.error || 'Failed to load monitoring metrics')
    }

    return response.data.data
  },

  async getSolanaSignerLaneStatus(
    mode: NetworkEnvironment
  ): Promise<SolanaSignerLaneStatusResponse> {
    const response = await adminApiClient.get<ApiResponse<SolanaSignerLaneStatusResponse>>(
      '/admin/monitor/solana-signer/lanes',
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(
        getApiErrorMessage(response.data, 'Failed to load Solana signer lane status')
      )
    }
    return response.data.data
  },

  async getSolanaSignerRpcHealth(
    mode: NetworkEnvironment
  ): Promise<SolanaSignerRpcHealthResponse> {
    const response = await adminApiClient.get<ApiResponse<SolanaSignerRpcHealthResponse>>(
      '/admin/monitor/solana-signer/rpc-health',
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(
        getApiErrorMessage(response.data, 'Failed to load Solana signer RPC health')
      )
    }
    return response.data.data
  },

  async reconcileSolanaSignerLane(
    expected: SolanaSignerLaneExpectation,
    reason: string,
    mode: NetworkEnvironment
  ): Promise<SolanaSignerLaneReconcileResult> {
    const response = await adminApiClient.post<
      ApiResponse<SolanaSignerLaneReconcileResult>
    >(
      '/admin/monitor/solana-signer/lanes/reconcile',
      { expected, reason },
      buildModeAwareConfig(mode)
    )
    if (!response.data?.success || !response.data.data) {
      throw new Error(
        getApiErrorMessage(response.data, 'Failed to coordinate Solana signer lane')
      )
    }
    return response.data.data
  },

  async getPlatformContracts(mode?: NetworkEnvironment): Promise<PlatformContractDescriptor[]> {
    const response = await adminApiClient.get<
      ApiResponse<{ contracts: PlatformContractDescriptor[] }>
    >('/admin/monitor/platform-contracts', buildModeAwareConfig(mode))

    if (!response.data?.data?.contracts) {
      throw new Error(response.data?.error || 'Failed to load platform contracts')
    }

    return response.data.data.contracts
  },

  async checkPlatformContractConsistency(
    mode?: NetworkEnvironment,
    params?: {
      chainId?: number
      chainCode?: string
      contractId?: string
    }
  ): Promise<PlatformContractConsistencyResult> {
    const response = await adminApiClient.post<
      ApiResponse<{ result: PlatformContractConsistencyResult }>
    >(
      '/admin/monitor/platform-contracts/consistency-check',
      params,
      buildModeAwareConfig(mode)
    )

    if (!response.data?.data?.result) {
      throw new Error(response.data?.error || 'Failed to check platform contracts')
    }

    return response.data.data.result
  },

  async getFactoryDetails(
    contractId: string,
    mode?: NetworkEnvironment
  ): Promise<FactoryReadOnlySnapshot> {
    const response = await adminApiClient.get<
      ApiResponse<{ factory: FactoryReadOnlySnapshot }>
    >('/admin/monitor/factory-details', {
      params: { contractId },
      ...buildModeAwareConfig(mode),
    })

    if (!response.data?.data?.factory) {
      throw new Error(response.data?.error || 'Failed to load factory details')
    }

    return response.data.data.factory
  },
}
